import { Fragment, useEffect, useState, useRef } from 'react'
import { toast } from 'react-hot-toast'
import {
  Control,
  FieldArrayWithId,
  FieldErrors,
  useFieldArray,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFormGetValues,
  UseFormRegister,
  UseFormReset,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form'
import {
  useActualizarTareaMutation,
  useEliminarTareaPorIdMutation,
  useGuardarTareaMutation,
  useRecuperarTareasPorIdRelacionadoMutation,
} from '../../../../../redux/api/tareas.api'
import { AppConstantes } from '../../../../../utils/Constantes'
import { style } from '../../../models/tareaCondicionanteForm.type'
import dayjs from 'dayjs'
import { useDispatch, useSelector } from 'react-redux'
import { cargarTareas } from '../../../../../redux/slices/tareas'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { Alert } from '../../../../../components/alerts/Alert'
import { RootState } from '../../../../../redux'
import { TCondicionanteForm } from '../../../models/condicionanteForm.type'
import { Condicionante } from '../../../models'
import { TareaCondicionanteModalCamposTareas } from './TareaCondicionanteModalCamposTareas'
import ComponentePrivado from '../../../../../components/roles/ComponentePrivado'
import { Roles } from '../../../../../utils/Roles'
import useHasPermiso from '../../../../../hooks/autenticacion/useHasPermiso'
import { Tarea } from '../../../../tareas/models'
import { TareaCondicionanteModalCamposRecodatorios } from './TareaCondicionanteModalCamposRecordatorios'
import { TRecordatorioForm } from '../../../../recordatorios/models/recordatorioForm.type'

type Props = {
  idSitio?: string
  idAutorizacion?: string
  idCondicionante?: string
  isModoEditar: boolean
  fechaVencimiento?: string
  condicionante?: Condicionante
  fields: FieldArrayWithId<TCondicionanteForm, 'tareas'>[]
  remove: UseFieldArrayRemove
  append: UseFieldArrayAppend<TCondicionanteForm, 'tareas'>
  control: Control<TCondicionanteForm>
  register: UseFormRegister<TCondicionanteForm>
  errors: FieldErrors<TCondicionanteForm>
  trigger: UseFormTrigger<TCondicionanteForm>
  reset: UseFormReset<TCondicionanteForm>
  getValues: UseFormGetValues<TCondicionanteForm>
  setValue: UseFormSetValue<TCondicionanteForm>
  watch: UseFormWatch<TCondicionanteForm>
  recalcularFechasInicio: () => void
  isSuccessTareas: boolean
}
export const TareaCondicionanteModal = ({
  idSitio,
  idAutorizacion,
  idCondicionante,
  isModoEditar,
  fields,
  remove,
  append,
  control,
  register,
  errors,
  trigger,
  getValues,
  setValue,
  watch,
  condicionante,
  fechaVencimiento,
  recalcularFechasInicio,
  isSuccessTareas,
}: Props) => {
  const { hasPermiso } = useHasPermiso()
  // Permiso de crear tareas controla acciones (alta/edicion/baja) dentro del modal.
  const usuarioPermisoTareas = hasPermiso([Roles.roleTareasCrear])

  // Este submodulo maneja tareas anidadas dentro de una condicionante.
  // Casos clave:
  // - En edicion: cada tarea puede guardarse/actualizarse individualmente via API.
  // - En creacion: las tareas viven solo en el form hasta guardar la condicionante.
  // - Recordatorios: solo se permite abrir/editar si la tarea ya existe en backend (tiene idTarea).

  const [indice, setIndice] = useState(-1)
  const { update } = useFieldArray({
    name: 'tareas',
    control,
  })

  const [guardarTarea, { data: dataTarea, isSuccess: isSuccessGuardar }] = useGuardarTareaMutation()
  const [actualizarTarea] = useActualizarTareaMutation()

  const [eliminarTareaPorId, { isLoading: isLoadingEliminar }] = useEliminarTareaPorIdMutation()
  const [recuperarTareas, isSuccess] = useRecuperarTareasPorIdRelacionadoMutation()

  const dispatch = useDispatch()
  const tareasRecuperadas = useSelector((state: RootState) => state.listaTareas)

  const [tareaCancelada, setTareaCancelada] = useState(false)

  const [indexIdTareaConRecordatoriosAbiertos, setIndexIdTareaConRecordatoriosAbiertos] = useState<
    string | undefined
  >(AppConstantes.CVE_MODAL_CONDICIONANTE_ESTADO_INICIAL)

  /**
   * @description Coloca en la parte inicial del campo scrolleable
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 21/12/2023
   */
  const refArriba = useRef<HTMLSpanElement>(null)
  const enviarAlInicio = () => {
    refArriba.current?.scrollIntoView({
      behavior: 'auto',
    })
  }

  /**
   * @description Coloca en la parte final del campo scrolleable
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 21/12/2023
   */
  const refAbajo = useRef<HTMLSpanElement>(null)
  const enviarAlFinal = () => {
    refAbajo.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    if (isSuccessGuardar) {
      // Cuando el backend asigna `idTarea`, se copia al form para habilitar acciones posteriores
      // (por ejemplo, guardar recordatorios o eliminar).
      const tarea = getValues().tareas[indice]
      update(indice, {
        ...tarea,
        idTarea: dataTarea.idTarea,
      })
    }

    isSuccessTareas && enviarAlInicio()
  }, [isSuccessGuardar, isSuccess, isSuccessTareas])

  /**
   * @description Calcula la cantidad de tareas en función a la periodicidad
   *              y agrega esa cantida de input sobre el formulario.
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 02/11/2022
   * @param index  Recibe el índice de los campos de tarea que se remueven
   */
  const eliminarTarea = (index: number) => {
    // Si la tarea existe en backend se elimina por API y luego se refetch.
    // Si aun no existe, se elimina solo del array del form.
    const tarea = getValues().tareas[index]
    if (tarea.idTarea) {
      const toastId = toast.loading('Eliminando Tarea', { style })
      eliminarTareaPorId(tarea.idTarea)
        .unwrap()
        .then(() => {
          remove(index)
          toast.success('Tarea eliminada', { id: toastId, style })
          recuperarTareas(`condicionante/${idCondicionante}`)
            .unwrap()
            .then((resp: Tarea[]) => {
              dispatch(cargarTareas(resp))
            })
            .catch(console.error)
        })
        .catch((error) => {
          const err = isModoEditar ? error : ''
          const mensaje = err.data ? err.data.moreInfo : err.error ? err.error : ''
          toast.error(`${mensaje}`, { id: toastId, style })
        })
    } else {
      // Si aun no existe en backend, solo se elimina de UI.
      remove(index)
    }
  }

  const actualizarGuardar = (i: number) => {
    // Guarda/actualiza una tarea individual:
    // - Primero valida el subform (trigger)
    // - Luego mapea Opcion->valor/etiqueta y formatea fechas para el contrato API
    // Trigger valida el sub-form de la tarea antes de mapearlo al contrato de API.
    trigger(`tareas.${i}`).then((isValido: boolean) => {
      if (isValido) {
        const tarea = getValues().tareas[i]
        const t: Tarea = {
          ...tarea,
          idTarea: tarea.idTarea,
          idSitio: idSitio,
          nombreSitio: condicionante?.nombreSitio,
          idAutorizacion: idAutorizacion,
          idCondicionante: idCondicionante,
          nombreCondicionante: condicionante?.nombre,
          descripcion: tarea.descripcion,
          idUsuarioAsignado: tarea.idUsuarioAsignado?.valor,
          estatus: tarea.estatus?.valor,
          descEstatus: tarea.estatus?.etiqueta,
          fechaInicio: dayjs(tarea.fechaInicio).format(AppConstantes.FORMATO_FECHA_POST),
          fechaTermino: dayjs(tarea.fechaTermino).format(AppConstantes.FORMATO_FECHA_POST),
          recordatorios: tarea.recordatorios?.map((r: TRecordatorioForm) => {
            return {
              ...r,
              cantidad: r?.cantidad,
              unidad: r.unidad?.valor,
              descUnidad: r.unidad?.etiqueta,
              // Para unidades no personalizadas el backend calcula en base a fecha de termino.
              fechaReferencia:
                r.unidad?.valor !== AppConstantes.CVE_UNIDAD_RECORDATORIO_PERSONALIZADO
                  ? tarea.fechaTermino
                  : null,
              fechaRecordatorio: r.fechaRecordatorio
                ? dayjs(r.fechaRecordatorio).format(AppConstantes.FORMATO_FECHA_RECORDATORIO_POST)
                : null,
            }
          }),
        }

        if (tarea.idTarea) {
          const toastId = toast.loading('Actualizando tarea', { style })
          actualizarTarea(t)
            .unwrap()
            .then(() => {
              toast.success('¡Tarea actualizada!', { id: toastId, style })
              recuperarTareas(`condicionante/${idCondicionante}`)
                .unwrap()
                .then((resp: Tarea[]) => {
                  dispatch(cargarTareas(resp))
                })
                .catch(console.error)
            })
            .catch(() => {
              toast.error('Error al actualizar la tarea', {
                id: toastId,
                style,
              })
              recuperarTareas(`condicionante/${idCondicionante}`)
                .unwrap()
                .then((resp: Tarea[]) => {
                  dispatch(cargarTareas(resp))
                })
                .catch(console.error)
            })
        } else {
          setIndice(i)
          const toastId = toast.loading('Guardando tarea', { style })
          guardarTarea(t)
            .unwrap()
            .then(() => {
              recuperarTareas(`condicionante/${idCondicionante}`)
                .unwrap()
                .then((resp: Tarea[]) => {
                  dispatch(cargarTareas(resp))
                })
                .catch(console.error)
              toast.success('Tarea guardada', {
                id: toastId,
                style,
              })
            })
            .catch(() => {
              toast.error('Error al guardar la tarea', {
                id: toastId,
                style,
              })
              recuperarTareas(`condicionante/${idCondicionante}`)
                .unwrap()
                .then((resp: Tarea[]) => {
                  dispatch(cargarTareas(resp))
                })
                .catch(console.error)
            })
        }
      }
    })
  }

  /**
   * @description Agrega campos para una tarea a fields con ayuda de append
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 04/11/2022
   */
  const agregarTareaExtra = () => {
    // Agrega una tarea manual adicional. En edicion exige que no existan tareas sin guardar.
    setIndexIdTareaConRecordatoriosAbiertos(
      AppConstantes.CVE_MODAL_CONDICIONANTE_ESTADO_NUEVA_TAREA
    )

    trigger('tareas').then((isValido: boolean) => {
      if (isValido && !errors.tareas) {
        append({
          titulo: '',
          descripcion: '',
          estatus: {
            valor: AppConstantes.CVE_ESTATUS_TAREA_EN_PROCESO,
            etiqueta: AppConstantes.DESC_ESTATUS_TAREA_EN_PROCESO,
          },
          fechaInicio: `${dayjs()}`,
          fechaTermino: '',
          idUsuarioAsignado: null,
          isManual: true,
        })
      }
    })
  }

  /**
   * @description Envia al campo de usuario asignado el string 'Usuario sin asignar'
   *              cuando se selecciona un 'POR ASIGNAR' en estatus.
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 21/02/2023
   * @param e opción del select de estatus.
   * @param index índice de la tarea en donde se hace la selección.
   */
  const alertTareaCancelada = (e: { valor: string; etiqueta: string; ext5: string | null }) => {
    if (e) {
      if (e.valor === AppConstantes.CVE_ESTATUS_TAREA_CANCELADA) {
        setTareaCancelada(true)
      } else {
        setTareaCancelada(false)
      }
    }
  }

  /**
   * @description Actualiza la lista de tareas en redux cada que se guarda un recordatorio
   *              asociado a una tarea.
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 30/03/2023
   */
  const actualizaListaDeTareasRedux = () => {
    recuperarTareas(`condicionante/${condicionante?.idCondicionante}`)
      .unwrap()
      .then((resp: Tarea[]) => {
        dispatch(cargarTareas(resp))
      })
      .catch(console.error)
  }

  const agregarCamposModoCreacion = (indexIdTarea?: string) => {
    trigger('tareas').then((isValido: boolean) => {
      if (!errors.tareas && isValido) {
        indexIdTareaConRecordatoriosAbiertos === indexIdTarea
          ? setIndexIdTareaConRecordatoriosAbiertos(
              AppConstantes.CVE_MODAL_CONDICIONANTE_ESTADO_INICIAL
            )
          : setIndexIdTareaConRecordatoriosAbiertos(indexIdTarea)
      } else {
        toast.error('Llena todos los campos antes de continuar.', {
          style,
        })
      }
    })
  }

  const generarMensajeTodosRecordatoriosGuardados = (indexIdTarea?: string) => {
    const RecordatoriosRegistradosSeleccionPasada = tareasRecuperadas?.tareas.filter((e: Tarea) =>
      e.idTarea.includes(indexIdTareaConRecordatoriosAbiertos)
    )[0].recordatorios?.length
    const RecordatoriosRegistradosSeleccionPasadaGetValues = getValues().tareas?.filter((e: any) =>
      e.idTarea.includes(indexIdTareaConRecordatoriosAbiertos)
    )[0]?.recordatorios?.length
    trigger('tareas').then((isValido: boolean) => {
      if (
        RecordatoriosRegistradosSeleccionPasada ===
          RecordatoriosRegistradosSeleccionPasadaGetValues &&
        isValido
      ) {
        indexIdTareaConRecordatoriosAbiertos === indexIdTarea
          ? setIndexIdTareaConRecordatoriosAbiertos(
              AppConstantes.CVE_MODAL_CONDICIONANTE_ESTADO_INICIAL
            )
          : setIndexIdTareaConRecordatoriosAbiertos(indexIdTarea)
      } else if (
        RecordatoriosRegistradosSeleccionPasada !==
          RecordatoriosRegistradosSeleccionPasadaGetValues &&
        isValido
      ) {
        toast.error('Existen recordatorios sin guardar.', {
          style,
        })
      } else if (
        RecordatoriosRegistradosSeleccionPasada !==
          RecordatoriosRegistradosSeleccionPasadaGetValues &&
        errors.tareas
      ) {
        toast.error('Llena todos los campos antes de continuar.', {
          style,
        })
      }
    })
  }
  /**
   * @description Verifica campos validos y errores antes de realizar alguna
   *              otra acción en recordatorios y tareas de una condicionante.
   *
   * @param index  Recibe el indice de la tarea
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 30/03/2023
   */
  const verificaRecordatoriosSinGuardar = (indexIdTarea: string | undefined) => {
    // Controla apertura/cierre del panel de recordatorios y previene perder cambios:
    // - En edicion: si hay tareas sin guardar o recordatorios sin persistir, bloquea el cambio.
    // - En creacion: solo valida integridad del form.
    if (isModoEditar && indexIdTarea) {
      if (
        indexIdTareaConRecordatoriosAbiertos ===
        AppConstantes.CVE_MODAL_CONDICIONANTE_ESTADO_INICIAL
      ) {
        setIndexIdTareaConRecordatoriosAbiertos(indexIdTarea)
      } else if (
        indexIdTareaConRecordatoriosAbiertos !==
        AppConstantes.CVE_MODAL_CONDICIONANTE_ESTADO_NUEVA_TAREA
      ) {
        const tamanoTotalTareasRegistradas = tareasRecuperadas?.tareas.length
        if (tamanoTotalTareasRegistradas === getValues().tareas.length) {
          generarMensajeTodosRecordatoriosGuardados(indexIdTarea)
        } else {
          toast.error('Guarda todas las tareas antes de continuar.', {
            style,
          })
        }
      } else if (
        indexIdTareaConRecordatoriosAbiertos ===
        AppConstantes.CVE_MODAL_CONDICIONANTE_ESTADO_NUEVA_TAREA
      ) {
        const tamanoTotalTareasRegistradas = tareasRecuperadas?.tareas.length
        if (tamanoTotalTareasRegistradas === getValues().tareas.length) {
          setIndexIdTareaConRecordatoriosAbiertos(indexIdTarea)
        } else {
          toast.error('Guarda todas la tareas antes de continuar.', {
            style,
          })
        }
      }
    } else if (!isModoEditar) {
      agregarCamposModoCreacion(indexIdTarea)
    }
  }

  /**
   * @description Valida campos existentes antes de agregar una tarea extra
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 31/03/2023
   */
  const validarCamposParaAgregarTareaExtra = () => {
    // Antes de agregar una tarea extra, valida que el set actual sea consistente.
    trigger('tareas').then((isValido: boolean) => {
      if (isModoEditar) {
        const tareasRegistradas = tareasRecuperadas?.tareas
        if (
          isValido &&
          tareasRegistradas &&
          tareasRegistradas.length === getValues().tareas.length
        ) {
          agregarTareaExtra()
        } else if (
          isValido &&
          tareasRegistradas &&
          tareasRegistradas.length !== getValues().tareas.length
        ) {
          !errors.tareas &&
            toast.error('Guarda todas las tareas antes de agregar una tarea.', {
              style,
            })
          errors.tareas &&
            toast.error('Llena todos los campos antes de agregar una tarea.', {
              style,
            })
        }
      } else if (!isModoEditar) {
        if (isValido) {
          agregarTareaExtra()
        } else {
          toast.error('Llena todos los campos antes de agregar una tarea.', {
            style,
          })
        }
      }
    })
  }

  return (
    <>
      <div className='col-12 '>
        <div className='align-items-center mb-6 d-flex flex-grow-1 '>
          <h5 className=' d-flex'>Tareas</h5>
          <span className='text-muted fw-lighter fs-4 ps-2 d-flex'>
            | {fields.length} total
          </span>{' '}
        </div>
      </div>{' '}
      <div>
        {fields.length === 0 ? (
          <div style={{ marginTop: '15vh', marginBottom: '15vh' }}>
            <div className=' d-flex justify-content-center ms-5'>
              <i className=' bi bi-book font-size-icon'></i>
            </div>
            <div className='d-flex justify-content-center ms-5 mt-2'>
              <i style={{ fontSize: '1.2em' }}>Sin tareas</i>
            </div>
          </div>
        ) : (
          <div className='col-12 row'>
            {tareaCancelada && (
              <Alert
                color='danger'
                mensaje={`Al actualizar una tarea con estatus 'cancelada' todos sus recordatorios y alertas serán eliminadas`}
              />
            )}

            <div style={{ width: '15%' }}>
              <label className='form-label fs-4 mb-3'>
                Título <span className='text-danger'>*</span>
              </label>
            </div>
            <div style={{ width: '25%' }}>
              <label className='form-label fs-4 mb-3'>
                Descripción<span className='text-danger'>*</span>
              </label>
            </div>
            <div style={{ width: '15%' }}>
              <label className='form-label fs-4 mb-3'>
                Estatus<span className='text-danger'>*</span>
              </label>
            </div>

            <div style={{ width: '18%' }}>
              <label className='form-label fs-4 mb-3'>
                Usuario asignado<span className='text-danger'>*</span>
              </label>
            </div>
            <div style={{ width: '10%' }}>
              <label className='form-label fs-4 mb-3'>
                Fecha inicio<span className='text-danger'>*</span>
              </label>
            </div>
            <div style={{ width: '10%' }}>
              <label className='form-label fs-4 mb-3'>
                Fecha término<span className='text-danger'>*</span>
              </label>
            </div>
            <div
              className='overflow-auto div-height-tareas-modal-condicionante px-0'
              id='div-modal-tareas'>
              <span ref={refArriba}></span>
              <div>
                {!isModoEditar ? (
                  <Alert
                    mensaje='Al cambiar la fecha de inicio y/o fecha de término de la primera tarea generada por periodicidad, se modificarán las fechas de las siguientes tareas (generadas por periodicidad) con base en estas nuevas primeras fechas seleccionadas.'
                    dismissible={true}
                  />
                ) : (
                  ''
                )}
                {fields.map(
                  (field: FieldArrayWithId<TCondicionanteForm, 'tareas'>, index: number) => (
                    <Fragment key={field.id}>
                      <div className='row mx-0'>
                        <TareaCondicionanteModalCamposTareas
                          idSitio={idSitio}
                          fechaVencimiento={fechaVencimiento}
                          index={index}
                          field={field}
                          alertTareaCancelada={alertTareaCancelada}
                          {...{
                            control,
                            register,
                            errors,
                            getValues,
                            setValue,
                          }}
                          usuarioPermiso={usuarioPermisoTareas}
                          recalcularFechasInicio={
                            recalcularFechasInicio
                          }></TareaCondicionanteModalCamposTareas>

                        <div className='d-flex' style={{ width: '7%' }}>
                          {(!isModoEditar ||
                            (isModoEditar &&
                              getValues().tareas[index].idTarea &&
                              getValues().tareas[index].estatus?.valor !==
                                AppConstantes.CVE_ESTATUS_TAREA_CANCELADA &&
                              getValues().tareas[index].estatus?.valor !==
                                AppConstantes.CVE_ESTATUS_TAREA_BAJA)) && (
                            <ComponentePrivado rolMostrarPorPermiso={[Roles.roleTareasCrear]}>
                              <OverlayTrigger
                                placement='top'
                                overlay={<Tooltip>Recordatorios</Tooltip>}>
                                <button
                                  className='btn btn-outline-primary btn-icon btn-circle btn-sm me-2'
                                  type='button'
                                  onClick={() => {
                                    isModoEditar
                                      ? verificaRecordatoriosSinGuardar(
                                          getValues().tareas[index].idTarea
                                        )
                                      : verificaRecordatoriosSinGuardar(index.toLocaleString())
                                  }}>
                                  <i className='fa-sharp fa-solid fa-bell fs-3'></i>
                                </button>
                              </OverlayTrigger>
                            </ComponentePrivado>
                          )}
                          {isModoEditar && (
                            <ComponentePrivado rolMostrarPorPermiso={[Roles.roleTareasCrear]}>
                              <OverlayTrigger
                                placement='top'
                                overlay={
                                  getValues().tareas[index].idTarea ? (
                                    <Tooltip>Actualizar tarea</Tooltip>
                                  ) : (
                                    <Tooltip>Guardar tarea</Tooltip>
                                  )
                                }>
                                <button
                                  className={`btn btn-outline-${
                                    getValues().tareas[index].idTarea ? 'success' : 'danger'
                                  } btn-icon btn-circle btn-sm me-2`}
                                  type='button'
                                  onClick={() => {
                                    actualizarGuardar(index)
                                  }}>
                                  <i className='fa-solid fa-check fs-1'></i>
                                </button>
                              </OverlayTrigger>
                            </ComponentePrivado>
                          )}
                          {!getValues().tareas[index].idTarea && (
                            <ComponentePrivado rolMostrarPorPermiso={[Roles.roleTareasCrear]}>
                              <OverlayTrigger
                                placement='top'
                                overlay={<Tooltip>Eliminar tarea</Tooltip>}>
                                <button
                                  className='btn btn-outline-danger btn-icon btn-circle btn-sm '
                                  disabled={isLoadingEliminar && isModoEditar}
                                  type='button'
                                  data-bs-toggle='tooltip'
                                  data-bs-placement='left'
                                  title='Eliminar tarea'
                                  onClick={() => {
                                    eliminarTarea(index)
                                  }}>
                                  <i className='fa-solid fa-minus fs-1'></i>
                                </button>
                              </OverlayTrigger>
                            </ComponentePrivado>
                          )}
                        </div>
                        <TareaCondicionanteModalCamposRecodatorios
                          isModoEditar={isModoEditar}
                          remove={remove}
                          append={append}
                          {...{
                            control,
                            register,
                            errors,
                            getValues,
                            watch,
                            trigger,
                          }}
                          index={index}
                          field={field}
                          indexIdTareaConRecordatoriosAbiertos={
                            indexIdTareaConRecordatoriosAbiertos
                          }
                          actualizaListaDeTareasRedux={
                            actualizaListaDeTareasRedux
                          }></TareaCondicionanteModalCamposRecodatorios>
                      </div>
                    </Fragment>
                  )
                )}
              </div>
              <span ref={refAbajo}></span>
            </div>
          </div>
        )}
        <div className='d-flex justify-content-center mx-2 mt-7'>
          <ComponentePrivado rolMostrarPorPermiso={[Roles.roleTareasCrear]}>
            <OverlayTrigger placement='top' overlay={<Tooltip>Agregar tarea</Tooltip>}>
              <button
                disabled={isModoEditar ? false : getValues('periodicidad') ? false : true}
                className='btn btn-outline-danger btn-icon btn-circle btn-sm'
                type='button'
                title='Agregar tarea'
                onClick={() => {
                  enviarAlFinal()
                  validarCamposParaAgregarTareaExtra()
                }}>
                <i className='fa-solid fa-plus fs-1'></i>
              </button>
            </OverlayTrigger>
          </ComponentePrivado>
        </div>
      </div>
    </>
  )
}
